import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context';
import MainLayout from '../components/shared/MainLayout';

// Eager load Auth pages (needed immediately)
import { LoginPage, RegisterPage } from '../pages/Auth';

// Lazy load route components for code splitting
const FeedPage = lazy(() => import('../pages/Feed/FeedPage'));
const MessagesPage = lazy(() => import('../pages/Messages/MessagesPage'));
const NetworkPage = lazy(() => import('../pages/Network/NetworkPage'));
const JobsPage = lazy(() => import('../pages/Jobs/JobsPage'));
const ProfilePage = lazy(() => import('../pages/Profile/ProfilePage'));
const SearchResultsPage = lazy(() => import('../pages/Search/SearchResultsPage'));
const OTPVerificationComponent = lazy(() => import('../components/OTPVerification'));
const ForgotPassword = lazy(() => import('../components/ForgotPassword'));
const PasswordResetOTP = lazy(() => import('../components/PasswordResetOTP'));
const VerifyEmail = lazy(() => import('../components/VerifyEmail'));
const ResetPassword = lazy(() => import('../components/ResetPassword'));
const NotificationsTab = lazy(() => import('../components/NotificationsTab'));
const SettingsTab = lazy(() => import('../components/SettingsTab'));
const CompaniesTab = lazy(() => import('../components/CompaniesTab'));
const MyPostsTab = lazy(() => import('../components/MyPostsTab'));

// OTP Verification Route Wrapper
const OTPVerification = () => {
  const [showEmailPrompt, setShowEmailPrompt] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [inputEmail, setInputEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    // Try to get email from sessionStorage first
    const storedEmail = sessionStorage.getItem('registrationEmail');
    if (storedEmail) {
      setEmail(storedEmail);
    } else {
      // No email found, show prompt
      setShowEmailPrompt(true);
    }
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!inputEmail) {
      setError('Please enter your email');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Import API_BASE_URL for use in this inline function
      const { API_BASE_URL } = await import('../config');

      // Resend OTP to the provided email
      const response = await fetch(`${API_BASE_URL}/auth/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inputEmail.toLowerCase() }),
      });

      const data = await response.json();

      if (response.ok) {
        setEmail(inputEmail.toLowerCase());
        sessionStorage.setItem('registrationEmail', inputEmail.toLowerCase());
        setShowEmailPrompt(false);
      } else {
        setError(data.error || 'Failed to send verification code');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerified = (data) => {
    // Store token and userId if provided
    if (data.token) {
      localStorage.setItem('token', data.token);
    }
    if (data.user && data.user.id) {
      localStorage.setItem('userId', data.user.id);
    }
    // Clear session storage
    sessionStorage.removeItem('registrationEmail');
    sessionStorage.removeItem('registrationFullName');
    // Redirect to feed
    window.location.href = '/feed';
  };

  const handleBack = () => {
    sessionStorage.removeItem('registrationEmail');
    sessionStorage.removeItem('registrationFullName');
    window.location.href = '/register';
  };

  if (showEmailPrompt) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Verify Your Email</h1>
            <p className="text-gray-600">
              Enter your email to receive a verification code
            </p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-900">
              {error}
            </div>
          )}

          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={inputEmail}
                onChange={(e) => setInputEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="your.email@example.com"
                disabled={loading}
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#4169E1] to-[#3557C5] text-white py-3 px-6 rounded-xl hover:shadow-lg transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending code...' : 'Send Verification Code'}
            </button>
            <button
              type="button"
              onClick={handleBack}
              className="w-full text-gray-600 hover:text-gray-900 py-2 transition-colors"
            >
              Back to Register
            </button>
          </form>
        </div>
      </div>
    );
  }

  const fullName = sessionStorage.getItem('registrationFullName') || '';

  return (
    <OTPVerificationComponent
      email={email}
      fullName={fullName}
      onVerified={handleVerified}
      onBack={handleBack}
      type="signup"
    />
  );
};

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin inline-block w-12 h-12 border-4 border-gray-300 border-t-[#4169E1] rounded-full mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Profile redirect wrapper - redirects /profile to /in/{customUrl or userNumber}
const ProfileRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Prefer custom URL if available, otherwise use userNumber
  const profileIdentifier = user?.customUrl || user?.userNumber;

  if (!profileIdentifier) {
    return <Navigate to="/feed" replace />;
  }

  return <Navigate to={`/in/${profileIdentifier}`} replace />;
};

// Public route wrapper (redirects to feed if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/feed" replace />;
  }

  return children;
};

const AppRoutes = () => {
  return (
    <MainLayout>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
        {/* Public routes */}
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/signup" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/verify-otp" element={<OTPVerification />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password-otp" element={<PasswordResetOTP />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Protected routes */}
        {/* Feed routes with sub-tabs */}
        <Route path="/feed" element={<Navigate to="/feed/discover" replace />} />
        <Route path="/feed/:feedMode" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />

        <Route path="/search/results/:filter" element={<ProtectedRoute><SearchResultsPage /></ProtectedRoute>} />
        <Route path="/messages" element={<Navigate to="/messages/connection" replace />} />
        <Route path="/messages/:type" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />

        {/* People routes with sub-tabs */}
        <Route path="/people" element={<Navigate to="/people/connections" replace />} />
        <Route path="/people/:tab" element={<ProtectedRoute><NetworkPage /></ProtectedRoute>} />

        {/* Jobs routes with sub-tabs */}
        <Route path="/jobs" element={<Navigate to="/jobs/browse" replace />} />
        <Route path="/jobs/:view" element={<ProtectedRoute><JobsPage /></ProtectedRoute>} />

        <Route path="/notifications" element={<ProtectedRoute><NotificationsTab /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsTab /></ProtectedRoute>} />
        <Route path="/companies" element={<ProtectedRoute><CompaniesTab /></ProtectedRoute>} />
        <Route path="/companies/:companyId" element={<ProtectedRoute><CompaniesTab /></ProtectedRoute>} />
        <Route path="/myposts" element={<ProtectedRoute><MyPostsTab /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfileRedirect /></ProtectedRoute>} />
        <Route path="/in/:identifier" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

          {/* Default route */}
          <Route path="/" element={<Navigate to="/feed" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
};

export default AppRoutes;
